import React, { FC, useEffect } from 'react';
import { Toast } from 'react-bootstrap';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

export type ToasterType = 'success' | 'error' | 'info' | 'warning';

interface ToasterProps {
    show: boolean;
    onClose: () => void;
    body: JSX.Element | string;
    autoHideTime?: number;
    iconName?: StsIconName;
    styles?: React.CSSProperties;
    textColor?: keyof typeof colors;
    type?: ToasterType;
}

const DEFAULT_AUTOHIDE_TIME = 3000;
const DEFAULT_TEXT_COLOR: keyof typeof colors = 'black';
const DEFAULT_ICON: StsIconName = 'x-close';

const typeStyles = {
    success: { icon: 'check-circle-hollow', color: 'greenOne' },
    error: { icon: 'error', color: 'redFour' },
    info: { icon: 'info', color: 'blueOne' },
    warning: { icon: 'warning', color: 'yellowFour' },
};

export const Toaster: FC<ToasterProps> = ({
    show,
    onClose,
    body,
    autoHideTime = DEFAULT_AUTOHIDE_TIME,
    iconName = DEFAULT_ICON,
    styles,
    textColor = DEFAULT_TEXT_COLOR,
    type,
}) => {
    useEffect(() => {
        if (show) {
            const timer = setTimeout(onClose, autoHideTime);
            return () => clearTimeout(timer);
        }
    }, [show, autoHideTime, onClose]);

    const typeIcon = iconName || (type ? typeStyles[type].icon : DEFAULT_ICON);
    const typeTextColor = textColor || (type ? typeStyles[type].color : DEFAULT_TEXT_COLOR);

    const mergedStyles: React.CSSProperties = {
        position: 'fixed',
        top: 70,
        left: '40%',
        right: '40%',
        zIndex: 9999,
        backgroundColor: colors.white,
        borderLeft: `5px solid ${colors[typeTextColor]}`,
        ...styles,
    };

    return (
        <Toast show={show} style={mergedStyles} onClose={onClose}>
            <Toast.Body>
                <div className="d-flex justify-content-between align-items-center pl-2">
                    <Typography variant="caption" color={typeTextColor}>
                        {body}
                    </Typography>
                    <Button variant="ghost-gray" onPress={onClose}>
                        <Icon name={typeIcon} color={typeTextColor} testID="toaster-icon" />
                    </Button>
                </div>
            </Toast.Body>
        </Toast>
    );
};
